import { CategoryValidation } from "@dreambigger/shared/src/types";

/**
 * Minimal shape the share-products submit gate needs from a rendered category.
 * The component passes full `ShareCategory` objects, which satisfy this
 * structurally.
 */
export interface GateCategory {
  slug: string;
  title: string;
}

/** A visible category whose configured minimum-selection count is unmet. */
export interface UnmetCategory {
  title: string;
  min: number;
}

/**
 * The core submit gate for the share-products step: at least one product must
 * be selected across the categories currently visible to the applicant.
 *
 * This is the invariant ACQR-9957 restored — it must hold even when
 * `categoryValidation` is configured with no per-category `min` (the case where
 * #4482's short-circuit let applicants advance with nothing selected). `counts`
 * is the live per-category selection count (category slug -> number selected).
 *
 * Only visible categories count: a selection persisted in a category that is
 * currently filtered out never satisfies the gate, so a changed prior answer
 * requires a fresh, visible selection.
 */
export function hasVisibleSelection(
  visibleCategories: Pick<GateCategory, "slug">[],
  counts: Record<string, number>
): boolean {
  return visibleCategories.some((category) => (counts[category.slug] || 0) > 0);
}

/**
 * Visible categories whose configured `min` has not yet been met. Categories
 * without a positive `min` never appear here (`min` is optional), and categories
 * that aren't visible are never enforced. Note this alone does NOT require any
 * selection — a step with no `min` on any category returns `[]` even at zero
 * selections, which is exactly why {@link hasVisibleSelection} exists.
 */
export function getUnmetVisibleCategories(
  visibleCategories: GateCategory[],
  categoryValidation: CategoryValidation[] | undefined,
  counts: Record<string, number>
): UnmetCategory[] {
  if (!categoryValidation) return [];
  return visibleCategories.reduce<UnmetCategory[]>((unmet, category) => {
    const validation = categoryValidation.find((v) => v.slug === category.slug);
    if (validation?.min && (counts[category.slug] || 0) < validation.min) {
      unmet.push({ title: category.title, min: validation.min });
    }
    return unmet;
  }, []);
}

/**
 * Human-readable submit error listing each unmet visible category and its
 * required minimum, e.g. "Please select at least 2 products from Checking and
 * at least 1 product from Savings to continue."
 */
export function buildMinSelectionError(unmet: UnmetCategory[]): string {
  const phrases = unmet.map(
    ({ title, min }) =>
      `at least ${min} ${min > 1 ? "products" : "product"} from ${title}`
  );
  const list =
    phrases.length > 1
      ? `${phrases.slice(0, -1).join(", ")} and ${phrases[phrases.length - 1]}`
      : phrases[0];
  return `Please select ${list} to continue.`;
}
